import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { Prescription } from '../../../types/models/Presc'
import { toCircledNum } from '../../../helpers/toCircledNum'
import { colorTokens } from '../../../shared/styles/theme'
import { isQrOnly } from '../../medilab/latest/list/queue'
import { FieldRow } from '../../medilab/latest/detail/FieldRow'
import {
  BASIC_RESULT_ROWS,
  REQUEST_GROUP_AT_BOTTOM,
  FIELD_LABELS,
  FieldGroup,
  REQUEST_FIELD_KEYS,
  formatBasicResultValue,
} from '../../medilab/latest/fields'
import { ReportTarget, reportLabel } from './report/labels'
import { useAnnotatorReport } from './report/useAnnotatorReport'

/**
 * annotator の閲覧（latest UI）の項目ペイン。**値は読むだけ・旗は立てられる**。
 *
 * 行は顧客側 FieldRow をそのまま借りる（editor / onSave を渡さないので値は直せない。
 * 値を click すると「この項目は編集できません(報告はできます)」の一文で応える）。
 * 修正を開けないのは、店の「修正」が新しい版 + JAHIS CSV + QR を作る店の記録だから（log/62 §2 E）。
 *
 * 右 gutter の旗は店と同じ位置・同じ見た目で、実体は店と同じ OCRError。ただし backend が
 * `reported_by` で割るので、**この面が立てる・降ろすのは annotator 起源の行だけ**。
 * 行の塗りは「店の旗（JSON の is_reported）or annotator の旗」の合成:
 *
 * - annotator が立てている欄 → click で降ろす
 * - 店だけが立てている欄 → click では何も変えず「店舗が報告した欄です」と行で応える
 * - どちらも無い欄 → click で立てる
 *
 * 顧客側から借りるのは props だけの純粋部品（FieldRow / ReportToggle / Note）で、
 * useEditField / useReportField / DetailFooter は import しない（ADR-0034・log/58 §1）。
 * 並び・ラベル・コード値のラベル化は顧客側と同じ fields.ts を共有する。
 * **この 2 ファイルを annotator の都合で書き換えないこと。**
 */

interface Props {
  prescription: Prescription
}

const Heading = ({ children }: { children: string }) => (
  <Typography
    sx={{ mt: 2.5, mb: 0.5, fontSize: 12, color: colorTokens.fg.faint }}
  >
    {children}
  </Typography>
)

const Empty = () => (
  <Typography sx={{ py: 0.5, fontSize: 14, color: colorTokens.fg.faint }}>
    (なし)
  </Typography>
)

const STORE_REPORTED = '店舗が報告した欄です'

export const ItemsPane = ({ prescription }: Props) => {
  const { isFlagged, setFlag, errorOn } = useAnnotatorReport(
    prescription.ocr_request_event_id
  )
  // 店だけが立てている欄を押したときの一言。直近の 1 行にだけ出す（紙を切り替えると消える）
  const [storeNoteLabel, setStoreNoteLabel] = useState<string | null>(null)

  const basic = prescription.basic_result
  const rpUnits = prescription.med_result ?? []

  /** 行の塗りと click を、店の旗と annotator の旗から決める */
  const flagProps = (target: ReportTarget, storeReported: boolean) => {
    const label = reportLabel(target)
    const mine = isFlagged(target)
    return {
      isReported: mine || storeReported,
      onToggle: () => {
        if (mine) {
          setStoreNoteLabel(null)
          setFlag(target, false)
        } else if (storeReported) {
          setStoreNoteLabel(label)
        } else {
          setStoreNoteLabel(null)
          setFlag(target, true)
        }
      },
      note: storeNoteLabel === label ? STORE_REPORTED : errorOn(target),
    }
  }

  const renderGroup = (group: FieldGroup, isSideBySide: boolean) =>
    group.keys.map((key) => {
      const field = basic?.[key]
      if (!field) return null
      const isRequest = REQUEST_FIELD_KEYS.includes(key)
      return (
        <FieldRow
          key={key}
          label={FIELD_LABELS[key] ?? key}
          labelWidth={isSideBySide ? 88 : 116}
          value={
            isRequest
              ? undefined
              : formatBasicResultValue(key, field.selected_option)
          }
          {...flagProps({ scope: 'basic', key }, field.is_reported)}
        />
      )
    })

  // 漏れた薬には行が無いので、店と同じく処方箋に 1 件だけ「どこかに漏れがある」を置く（RP1 に書く）
  const firstRp = rpUnits[0]

  return (
    <Box sx={{ py: 2, px: 3 }}>
      {basic ? (
        BASIC_RESULT_ROWS.map((groups) => (
          <Box
            key={groups.map((group) => group.heading).join('/')}
            sx={{ display: 'flex', gap: 3 }}
          >
            {groups.map((group) => (
              <Box key={group.heading} sx={{ flex: 1, minWidth: 0 }}>
                <Heading>{group.heading}</Heading>
                {renderGroup(group, groups.length > 1)}
              </Box>
            ))}
          </Box>
        ))
      ) : isQrOnly(prescription) ? (
        <Typography sx={{ py: 1, fontSize: 14, color: colorTokens.fg.muted }}>
          JAHIS QR から直接読み取ったため、項目ごとの読み取り結果はありません
        </Typography>
      ) : (
        <Empty />
      )}

      {basic && (
        <Box>
          {rpUnits.length === 0 && (
            <>
              <Heading>処方内容</Heading>
              <Empty />
            </>
          )}
          {rpUnits.map((rp, rpIndex) => (
            <Box key={rpIndex}>
              <Heading>
                {`処方内容${toCircledNum(
                  Number(rp['RP番号'].selected_option)
                )}`}
              </Heading>
              {rp.med_result.map((med, medIndex) => (
                <Box key={medIndex}>
                  <FieldRow
                    label="医薬品名"
                    value={med['薬品名称'].selected_option}
                    {...flagProps(
                      { scope: 'med', rpIndex, medIndex, key: '薬品名称' },
                      med['薬品名称'].is_reported
                    )}
                  />
                  <FieldRow
                    label="用量"
                    value={`${med['用量'].selected_option}${med['単位名'].selected_option}`}
                    {...flagProps(
                      { scope: 'med', rpIndex, medIndex, key: '用量' },
                      med['用量'].is_reported
                    )}
                  />
                </Box>
              ))}
              <FieldRow
                label="用法"
                value={rp['用法名称'].selected_option}
                {...flagProps(
                  { scope: 'rp', rpIndex, key: '用法名称' },
                  rp['用法名称'].is_reported
                )}
              />
              <FieldRow
                label="コメント"
                value={rp['コメント'].selected_option}
                {...flagProps(
                  { scope: 'rp', rpIndex, key: 'コメント' },
                  rp['コメント'].is_reported
                )}
              />
              <FieldRow
                label="調剤数量"
                value={rp['調剤数量'].selected_option}
                {...flagProps(
                  { scope: 'rp', rpIndex, key: '調剤数量' },
                  rp['調剤数量'].is_reported
                )}
              />
            </Box>
          ))}
        </Box>
      )}

      {basic && (
        <Box>
          <Heading>{REQUEST_GROUP_AT_BOTTOM.heading}</Heading>
          {renderGroup(REQUEST_GROUP_AT_BOTTOM, false)}
          {firstRp && (
            <FieldRow
              label="医薬品漏れ"
              {...flagProps(
                { scope: 'rp', rpIndex: 0, key: '医薬品取得' },
                !!firstRp['医薬品取得']?.is_reported
              )}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
